import {
  PersistedSurveyQuestion,
  ArdoqId,
  APISurveyAnswer,
  APIFieldType,
  SurveyQuestionType,
} from '@ardoq/api-types';
import { Answer, Answers, Fields, ReferenceAnswerValue } from '../types';
import {
  formatDateISO,
  currentDate,
  currentDateTimeISO,
} from '@ardoq/date-time';
import {
  isHiddenQuestion,
  isConditionQuestion,
  isReferenceQuestion,
  isAnswerableQuestion,
} from '../questions/questionPredicates';
import {
  getFieldQuestionAnswer,
  getConditionalFieldQuestionAnswers,
} from './answerOperations';
import { getSubQuestions } from '../questions/questionOperations';
import { getFieldType } from './fieldOperations';
import { isTruthy } from '@ardoq/common-helpers';

const getValueForQuestion = (
  question: PersistedSurveyQuestion,
  answer: Answer | undefined,
  currentUserId: ArdoqId,
  maintainHiddenQuestionValues: boolean
) => {
  const { type, properties } = question;
  if (
    type === SurveyQuestionType.HIDDEN_FIELD &&
    !maintainHiddenQuestionValues
  ) {
    if (properties.resetValue !== undefined) return properties.resetValue;
    if (properties.fieldType === APIFieldType.DATE_ONLY)
      return formatDateISO(currentDate());
    if (properties.fieldType === APIFieldType.DATE_TIME)
      return currentDateTimeISO();
    if (properties.fieldType === APIFieldType.USER) return currentUserId;
  }
  if (answer) {
    const value = answer.value;
    return value === '' || value === undefined ? null : value;
  }
  return null;
};

const addFieldTypeToQuestion = (
  question: PersistedSurveyQuestion,
  fields: Fields
) => {
  if (!isHiddenQuestion(question) || !question.properties.fieldName || !fields)
    return question;
  question.properties.fieldType = getFieldType(
    question.properties.fieldName,
    fields
  );
  return question;
};

type getAPISurveyAnswerArgs = {
  question: PersistedSurveyQuestion;
  answers: Answers;
  currentUserId: ArdoqId;
  maintainHiddenQuestionValues?: boolean;
};

export const getAPISurveyAnswer = ({
  question,
  answers,
  currentUserId,
  maintainHiddenQuestionValues = false,
}: getAPISurveyAnswerArgs): APISurveyAnswer => {
  let value = getValueForQuestion(
    question,
    answers?.[question._id],
    currentUserId,
    maintainHiddenQuestionValues
  );

  if (isConditionQuestion(question)) {
    const parentAnswer = getFieldQuestionAnswer(question._id, answers);
    const nestedAnswers = getConditionalFieldQuestionAnswers(parentAnswer);
    const nestedQuestions = getSubQuestions(question);
    return {
      questionId: question._id,
      type: question.type,
      value,
      answers: nestedQuestions
        .filter(isAnswerableQuestion)
        .filter(question => Object.keys(nestedAnswers).includes(question._id))
        .map(nestedQuestion =>
          getAPISurveyAnswer({
            question: nestedQuestion,
            answers: nestedAnswers,
            currentUserId,
            maintainHiddenQuestionValues,
          })
        ),
    };
  }

  if (isReferenceQuestion(question) && answers[question._id]) {
    value = value.map((refAnswer: ReferenceAnswerValue) => {
      const nestedQuestions = getSubQuestions(question);
      return {
        component: refAnswer.component,
        referenceId: refAnswer.referenceId,
        outgoing: refAnswer.outgoing,
        answers: nestedQuestions
          .filter(isAnswerableQuestion)
          .map(nestedQuestion =>
            refAnswer.answers[nestedQuestion._id]
              ? getAPISurveyAnswer({
                  question: nestedQuestion,
                  answers: refAnswer.answers,
                  currentUserId,
                  maintainHiddenQuestionValues,
                })
              : null
          )
          .filter(isTruthy),
      };
    });
  }

  return {
    questionId: question._id,
    type: question.type,
    value,
  };
};

type getAPISurveyAnswersArgs = Omit<getAPISurveyAnswerArgs, 'question'> & {
  questions: PersistedSurveyQuestion[];
  fields: Fields;
};

const isAnswerableOrHasAnswerableDependentQuestion = (
  question: PersistedSurveyQuestion
): boolean =>
  isAnswerableQuestion(question) ||
  (isConditionQuestion(question) &&
    getSubQuestions(question).some(isAnswerableQuestion));

export const getAPISurveyAnswers = ({
  questions,
  answers,
  currentUserId,
  fields,
  maintainHiddenQuestionValues = false,
}: getAPISurveyAnswersArgs): APISurveyAnswer[] => {
  return questions
    .filter(isAnswerableOrHasAnswerableDependentQuestion)
    .map(question => addFieldTypeToQuestion(question, fields))
    .map(question =>
      getAPISurveyAnswer({
        question,
        answers,
        currentUserId,
        maintainHiddenQuestionValues,
      })
    );
};
